import { useEffect, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ListRow } from "@/components/ui/ListRow";
import { LoadingLogo } from "@/components/ui/LoadingLogo";
import { SearchField } from "@/components/ui/SearchField";
import { Select } from "@/components/ui/Select";
import { fieldHints, fieldQuery, matches, placeholderFor, toggleField, type Searchable } from "@/lib/search";
import { useSessionState } from "@/lib/useSessionState";
import { twMerge } from "@/lib/twMerge";

// The one browse layout every list tab shares (Registry, Overview, Standards, Tools,
// Delivery briefs, Review): a grid of cards with a search box (and an optional filter pulldown),
// then — once an item is open — its detail beside a sticky, searchable list of the rest.
//
// The open item is in the URL (each page routes `/<tab>/<id>` here), so it can be linked and
// reloaded. The search and filter are kept for the tab (sessionStorage), so leaving an item
// returns to the same filtered list; a pill on a detail page sets the search (`onSearch`).

/** One choice in the filter pulldown. Options with a `group` are listed under that heading. */
export interface FilterOption {
  value: string;
  label: string;
  group?: string;
}

export interface BrowsePageProps<T> {
  title: string;
  /** Right of the title: counts, when it last changed. */
  meta?: ReactNode;
  /** Shown under the title above the grid (not beside a detail) — a reminder of what the tab is. */
  intro?: ReactNode;
  items: T[];
  itemKey: (item: T) => string;
  /** Where opening an item goes — usually this tab's `/<tab>/<id>`, but a tab without its own
   * detail (Overview) can send people to another's. */
  itemPath: (item: T) => string;
  /** The open item's key, from the URL; null for the grid. */
  selectedKey: string | null;
  /** The tab's own path, for "back" and for the URL of the grid. */
  basePath: string;
  searchable: (item: T) => Searchable;
  /** Readable labels for `field:value` suggestions whose values aren't self-explanatory. */
  hintLabels?: Record<string, Record<string, string>>;
  /** Prefix for the sessionStorage keys that remember the search and filter. */
  storageKey: string;
  /** An optional pulldown applied before the search. Its first option should be "everything". */
  filter?: { options: FilterOption[]; passes: (item: T, value: string) => boolean };
  card: (item: T) => ReactNode;
  /** An item's line in the side list. */
  row: (item: T) => ReactNode;
  /** The open item's detail. `onSearch(field, value, toggle)` lets its pills set the search
   * (or, with `toggle`, add or remove their term). Omit for a
   * tab whose cards open another tab's page. */
  detail?: (item: T, onSearch: (field: string, value: string, toggle?: boolean) => void) => ReactNode;
  /** Grid columns for the cards: 3 for short cards, 1 for long prose. */
  columns?: 1 | 2 | 3;
  /** What to call the items in messages ("tools", "capabilities"). */
  noun: string;
  /** Shown instead of the grid when there are no items at all. */
  empty?: ReactNode;
}

/** The shared browse layout. See the module comment. */
export function BrowsePage<T>({
  title,
  meta,
  intro,
  items,
  itemKey,
  itemPath,
  selectedKey,
  basePath,
  searchable,
  hintLabels,
  storageKey,
  filter,
  card,
  row,
  detail,
  columns = 3,
  noun,
  empty,
}: BrowsePageProps<T>) {
  const navigate = useNavigate();
  const [query, setQuery] = useSessionState(`${storageKey}.query`, "");
  const [filterValue, setFilterValue] = useSessionState(`${storageKey}.filter`, filter?.options[0]?.value ?? "");
  const selectedRow = useRef<HTMLLIElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const detailPane = useRef<HTMLDivElement>(null);

  // Each item opens at the top of its detail. The detail scrolls on its own (see the layout
  // below), so its scroll position would otherwise carry over from the previous item.
  useEffect(() => {
    detailPane.current?.scrollTo({ top: 0 });
  }, [selectedKey]);

  // Bring the open item's row into the side list's view only when it's out of view — on
  // arriving from a link, say. A row clicked in the list is already visible, so the list stays
  // put (re-centring it there made the list jump). The list is scrolled directly, never the
  // window. A block body: an effect must not return anything but a cleanup function.
  useEffect(() => {
    const r = selectedRow.current;
    const ul = list.current;
    if (!r || !ul) return;
    const top = r.offsetTop - ul.offsetTop;
    if (top < ul.scrollTop || top + r.clientHeight > ul.scrollTop + ul.clientHeight) {
      ul.scrollTop = top - ul.clientHeight / 2 + r.clientHeight / 2;
    }
  }, [selectedKey]);

  const searchables = new Map(items.map((item) => [itemKey(item), searchable(item)]));
  const activeFilter = filter && filter.options.some((o) => o.value === filterValue) ? filterValue : "";
  const shown = items.filter(
    (item) =>
      (!filter || !activeFilter || filter.passes(item, activeFilter)) &&
      matches(searchables.get(itemKey(item))!, query),
  );
  const hints = fieldHints([...searchables.values()], hintLabels);
  const selected = selectedKey ? (items.find((item) => itemKey(item) === selectedKey) ?? null) : null;
  // A pill's click: replace the search with its term, or — ⌘-clicked — add the term to the
  // search, or take it out if it's already there.
  const onSearch = (field: string, value: string, toggle = false) =>
    setQuery(toggle ? toggleField(query, field, value) : fieldQuery(field, value));
  const filtering = query !== "" || (filter !== undefined && activeFilter !== (filter.options[0]?.value ?? ""));

  const header = (
    <div className="mb-4 flex items-baseline justify-between gap-4">
      <h2 className="font-[family-name:--font-display] text-2xl">{title}</h2>
      {meta && <div className="text-sm text-text/60">{meta}</div>}
    </div>
  );

  if (items.length === 0 && empty) {
    return (
      <div>
        {header}
        {empty}
      </div>
    );
  }
  if (selectedKey && !selected) {
    return (
      <div>
        {header}
        <p className="text-text/60">
          Nothing called “{selectedKey}” here.{" "}
          <button onClick={() => navigate(basePath)} className="cursor-pointer text-accent underline">
            Back to all {noun}
          </button>
        </p>
      </div>
    );
  }

  const groups = [...new Set((filter?.options ?? []).map((o) => o.group).filter(Boolean))] as string[];
  const pulldown = filter && (
    <Select value={activeFilter} onChange={setFilterValue} className="w-full">
      {filter.options
        .filter((o) => !o.group)
        .map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      {groups.map((g) => (
        <optgroup key={g} label={g}>
          {filter.options
            .filter((o) => o.group === g)
            .map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
        </optgroup>
      ))}
    </Select>
  );
  const search = (
    <SearchField
      aria-label={`Search ${noun}`}
      // On the grid, the box takes focus as the page appears so a reader can just start typing.
      // Not beside an open item: there, focus would be pulled off the detail being read.
      autoFocus={!selected || !detail}
      placeholder={placeholderFor(noun, hints)}
      hints={hints}
      value={query}
      onChange={setQuery}
    />
  );

  if (!selected || !detail) {
    return (
      <div>
        {header}
        {intro}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            {pulldown && <div className="w-64 shrink-0">{pulldown}</div>}
            <div className="max-w-96 flex-1">{search}</div>
            {filtering && (
              <span className="text-sm text-text/60">
                {shown.length} of {items.length}
              </span>
            )}
          </div>
          {shown.length === 0 && <p className="text-sm text-text/60">No {noun} match.</p>}
          <div
            className={twMerge(
              "grid gap-3",
              columns === 3 ? "grid-cols-3" : columns === 2 ? "grid-cols-2" : "grid-cols-1",
            )}
          >
            {shown.map((item) => (
              <button key={itemKey(item)} onClick={() => navigate(itemPath(item))} className="text-left">
                {card(item)}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // The detail view fills exactly the window below the top bar (60px) and the page's own
  // padding (28px above and below) — so the page itself never scrolls; the list and the
  // detail each scroll on their own. The whole list is always in view, with no scroll
  // handlers: it's plain layout.
  return (
    <div className="flex h-[calc(100vh-116px)] flex-col">
      <div className="shrink-0">{header}</div>
      <div className="grid min-h-0 flex-1 grid-cols-[296px_1fr] gap-6">
        <div className="flex min-h-0 flex-col gap-2">
          <button
            onClick={() => navigate(basePath)}
            className="w-full cursor-pointer rounded px-2.5 py-1 text-left text-sm text-accent"
          >
            ← Back to all {noun}
          </button>
          {pulldown}
          {search}
          <ul ref={list} className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pr-1">
            {shown.map((item) => (
              <li key={itemKey(item)} ref={itemKey(item) === selectedKey ? selectedRow : undefined}>
                <ListRow selected={itemKey(item) === selectedKey} onClick={() => navigate(itemPath(item))}>
                  {row(item)}
                </ListRow>
              </li>
            ))}
            {shown.length === 0 && <li className="px-2.5 text-sm text-text/60">No {noun} match.</li>}
          </ul>
        </div>
        <div ref={detailPane} className="min-h-0 overflow-y-auto pr-1">
          {detail(selected, onSearch)}
        </div>
      </div>
    </div>
  );
}

/** What a browse page shows before its data arrives: its title and intro straight away, so a
 * reader can start to take in the page, then the loading logo — or, if loading failed, the
 * error in its place. */
export function BrowseLoading({
  title,
  intro,
  noun,
  error,
}: {
  title: string;
  intro?: ReactNode;
  noun: string;
  error?: string;
}) {
  return (
    <div>
      <div className="mb-4">
        <h2 className="font-[family-name:--font-display] text-2xl">{title}</h2>
      </div>
      {intro}
      {error ? (
        <p className="text-warn">
          Couldn't load {noun}: {error}
        </p>
      ) : (
        <LoadingLogo label={`Loading ${noun}…`} />
      )}
    </div>
  );
}
